import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import {
  CardGiftcard as BenefitIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES } from '../lib/tables';
import { useAuth } from '../context/AuthContext';

export default function BenefitsManagement() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [students, setStudents] = useState([]);
  const [benefitUsages, setBenefitUsages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentBenefits, setStudentBenefits] = useState([]);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedUsage, setSelectedUsage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    let isMounted = true;
    
    const loadAsync = async () => {
      if (profile && profile.rol === 'coordonator' && isMounted) {
        await loadData(isMounted);
      }
    };
    
    loadAsync();
    return () => { isMounted = false; };
  }, [profile]);

  const loadData = async (isMounted = true) => {
    setLoading(true);
    try {
      // incarca toate utilizarile de beneficii
      const { data: usagesData, error: usagesError } = await supabase
        .from(TABLES.benefit_usage)
        .select('*')
        .order('data_utilizare', { ascending: false });

      if (usagesError) throw usagesError;
      if (!isMounted) return;

      // incarca beneficiile separat
      const { data: benefitsData } = await supabase
        .from(TABLES.benefits)
        .select('*');

      if (!isMounted) return;

      // incarca elevii
      const { data: studentsData } = await supabase
        .from(TABLES.profiles)
        .select('*')
        .eq('rol', 'elev')
        .order('clasa')
        .order('nume');

      if (!isMounted) return;

      // combina datele
      const usagesWithDetails = (usagesData || []).map(usage => ({
        ...usage,
        benefit: benefitsData?.find(b => b.id === usage.id_beneficiu),
        student: studentsData?.find(s => s.id === usage.id_elev),
      }));

      setBenefitUsages(usagesWithDetails);

      // credite transferabile folosite, per elev
      const studentsWithUsage = await Promise.all(
        (studentsData || []).map(async (student) => {
          // creditele folosite pentru beneficii
          const studentUsages = (usagesData || []).filter(u => u.id_elev === student.id);
          const crediteUtilizate = studentUsages.reduce((sum, u) => sum + (u.credite_utilizate || 0), 0);
          
          // creditele transferabile totale
          const { data: categoryData } = await supabase
            .from(TABLES.credits_by_category)
            .select('credite_transferabile')
            .eq('id_elev', student.id)
            .maybeSingle();

          return {
            ...student,
            crediteTransferabile: categoryData?.credite_transferabile || 0,
            crediteUtilizate,
            beneficiiActive: studentUsages.filter(u => u.status === 'activ').length,
            beneficiiConsumate: studentUsages.filter(u => u.status === 'consumat').length,
          };
        })
      );

      if (isMounted) {
        setStudents(studentsWithUsage.filter(s => s.crediteUtilizate > 0 || s.beneficiiActive > 0));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      if (isMounted) {
        showSnackbar('Eroare la încărcarea datelor', 'error');
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenStudentDetails = async (student) => {
    setSelectedStudent(student);
    
    // incarca utilizarile acestui elev
    const { data: usageData } = await supabase
      .from(TABLES.benefit_usage)
      .select('*')
      .eq('id_elev', student.id)
      .order('data_utilizare', { ascending: false });

    // incarca beneficiile separat
    const { data: benefitsData } = await supabase
      .from(TABLES.benefits)
      .select('*');

    // combina datele
    const usagesWithBenefits = (usageData || []).map(usage => ({
      ...usage,
      benefit: benefitsData?.find(b => b.id === usage.id_beneficiu),
    }));

    setStudentBenefits(usagesWithBenefits);
    setDetailsDialogOpen(true);
  };

  const handleMarkAsConsumed = async (usage) => {
    try {
      const { error } = await supabase
        .from(TABLES.benefit_usage)
        .update({ 
          status: 'consumat',
          data_utilizare: new Date().toISOString()
        })
        .eq('id', usage.id);

      if (error) throw error;

      showSnackbar('Beneficiul a fost marcat ca utilizat!', 'success');
      
      // reincarca datele
      if (selectedStudent) {
        handleOpenStudentDetails(selectedStudent);
      }
      loadData();
    } catch (error) {
      console.error('Error:', error);
      showSnackbar('Eroare: ' + error.message, 'error');
    }
  };

  const handleDeleteUsage = (usage) => {
    setSelectedUsage(usage);
    setConfirmDialogOpen(true);
  };

  const confirmDeleteUsage = async () => {
    if (!selectedUsage) return;

    try {
      // sterge utilizarea
      const { error: deleteError } = await supabase
        .from(TABLES.benefit_usage)
        .delete()
        .eq('id', selectedUsage.id);

      if (deleteError) throw deleteError;

      // returneaza creditele in credits_by_category
      const { data: currentCredits } = await supabase
        .from(TABLES.credits_by_category)
        .select('credite_transferabile')
        .eq('id_elev', selectedUsage.id_elev)
        .maybeSingle();

      if (currentCredits) {
        await supabase
          .from(TABLES.credits_by_category)
          .update({
            credite_transferabile: (currentCredits.credite_transferabile || 0) + selectedUsage.credite_utilizate
          })
          .eq('id_elev', selectedUsage.id_elev);
      }

      showSnackbar('Beneficiul a fost șters și creditele returnate!', 'success');
      setConfirmDialogOpen(false);
      setSelectedUsage(null);
      
      // reincarca datele
      if (selectedStudent) {
        handleOpenStudentDetails(selectedStudent);
      }
      loadData();
    } catch (error) {
      console.error('Error:', error);
      showSnackbar('Eroare: ' + error.message, 'error');
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'activ':
        return <Chip label="Activ" color="success" size="small" />;
      case 'consumat':
        return <Chip label="Consumat" color="default" size="small" />;
      case 'expirat':
        return <Chip label="⏰ Expirat" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const filteredStudents = students.filter(s => 
    `${s.nume} ${s.prenume} ${s.clasa}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsages = benefitUsages.filter(u => {
    const studentName = `${u.student?.nume || ''} ${u.student?.prenume || ''}`;
    return studentName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (profile?.rol !== 'coordonator') {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Alert severity="error">
          Doar coordonatorii pot accesa această pagină.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}>
            <BenefitIcon fontSize="large" />
          </Avatar>
          Gestiune Beneficii
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Vizualizează și gestionează beneficiile revendicate de elevi
        </Typography>
      </Box>

      {/* Statistici rapide */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%', mb: 3 }}>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ bgcolor: '#e3f2fd', borderRadius: 3, height: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ textAlign: 'center', py: 0, p: 0 }}>
              <Typography variant="h3" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                {benefitUsages.length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                Total Revendicări
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ bgcolor: '#e8f5e9', borderRadius: 3, height: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ textAlign: 'center', py: 0, p: 0 }}>
              <Typography variant="h3" fontWeight="bold" color="success.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                {benefitUsages.filter(u => u.status === 'activ').length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                Active
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ bgcolor: '#fff3e0', borderRadius: 3, height: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ textAlign: 'center', py: 0, p: 0 }}>
              <Typography variant="h3" fontWeight="bold" color="warning.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                {benefitUsages.filter(u => u.status === 'consumat').length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                Consumate
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' } }}>
          <Card sx={{ bgcolor: '#fce4ec', borderRadius: 3, height: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ textAlign: 'center', py: 0, p: 0 }}>
              <Typography variant="h3" fontWeight="bold" color="error.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                {benefitUsages.reduce((sum, u) => sum + (u.credite_utilizate || 0), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                Credite Folosite
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Search & Refresh */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Caută elev..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={loadData}
        >
          Reîncarcă
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Pe Elevi" icon={<PersonIcon />} iconPosition="start" />
        <Tab label="Toate Revendicările" icon={<HistoryIcon />} iconPosition="start" />
      </Tabs>

      {/* Tab 1: Pe Elevi */}
      {tabValue === 0 && (
        <>
          {filteredStudents.length === 0 ? (
            <Alert severity="info">
              Nu există elevi cu beneficii revendicate.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {filteredStudents.map((student) => (
                <Grid item xs={12} md={6} lg={4} key={student.id}>
                  <Card 
                    sx={{ 
                      borderRadius: 3, 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { 
                        transform: 'translateY(-4px)',
                        boxShadow: 4 
                      }
                    }}
                    onClick={() => handleOpenStudentDetails(student)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {student.nume?.[0]}{student.prenume?.[0]}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {student.nume} {student.prenume}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {student.clasa} • {student.specializare}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#fff3e0', borderRadius: 2 }}>
                            <Typography variant="h6" fontWeight="bold" color="warning.main">
                              {student.crediteUtilizate}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Credite folosite
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                              {student.beneficiiActive}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Beneficii active
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {student.beneficiiConsumate > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                          + {student.beneficiiConsumate} consumate
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Tab 2: Toate Revendicările */}
      {tabValue === 1 && (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Elev</strong></TableCell>
                <TableCell><strong>Beneficiu</strong></TableCell>
                <TableCell align="center"><strong>Credite</strong></TableCell>
                <TableCell><strong>Data</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Acțiuni</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsages.map((usage) => (
                <TableRow key={usage.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {usage.student?.nume?.[0]}{usage.student?.prenume?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {usage.student?.nume} {usage.student?.prenume}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {usage.student?.clasa}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {usage.benefit?.nume_beneficiu || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {usage.benefit?.descriere?.substring(0, 40)}...
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={`-${usage.credite_utilizate}`} 
                      color="warning" 
                      size="small"
                      icon={<TrendingDownIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(usage.data_utilizare).toLocaleDateString('ro-RO')}
                  </TableCell>
                  <TableCell align="center">
                    {getStatusChip(usage.status)}
                  </TableCell>
                  <TableCell align="center">
                    {usage.status === 'activ' && (
                      <Tooltip title="Marchează ca utilizat">
                        <IconButton 
                          size="small" 
                          color="success"
                          onClick={() => handleMarkAsConsumed(usage)}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Șterge și returnează credite">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteUsage(usage)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nu există revendicări de beneficii
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog detalii elev */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {selectedStudent?.nume?.[0]}{selectedStudent?.prenume?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6">
                {selectedStudent?.nume} {selectedStudent?.prenume}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedStudent?.clasa} • {selectedStudent?.specializare}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Sumar credite */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <Card sx={{ bgcolor: '#e3f2fd', textAlign: 'center', p: 2 }}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {selectedStudent?.crediteTransferabile || 0}
                </Typography>
                <Typography variant="caption">Credite disponibile</Typography>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ bgcolor: '#fff3e0', textAlign: 'center', p: 2 }}>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {selectedStudent?.crediteUtilizate || 0}
                </Typography>
                <Typography variant="caption">Credite folosite</Typography>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ bgcolor: '#e8f5e9', textAlign: 'center', p: 2 }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {selectedStudent?.beneficiiActive || 0}
                </Typography>
                <Typography variant="caption">Beneficii active</Typography>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Istoric Beneficii
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Beneficiu</strong></TableCell>
                  <TableCell align="center"><strong>Credite</strong></TableCell>
                  <TableCell><strong>Data</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Acțiuni</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentBenefits.map((usage) => (
                  <TableRow key={usage.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {usage.benefit?.nume_beneficiu}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={`-${usage.credite_utilizate}`} color="warning" size="small" />
                    </TableCell>
                    <TableCell>
                      {new Date(usage.data_utilizare).toLocaleDateString('ro-RO')}
                    </TableCell>
                    <TableCell align="center">
                      {getStatusChip(usage.status)}
                    </TableCell>
                    <TableCell align="center">
                      {usage.status === 'activ' && (
                        <Tooltip title="Marchează ca utilizat">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleMarkAsConsumed(usage)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Șterge și returnează credite">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteUsage(usage)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {studentBenefits.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        Nicio revendicare
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Închide</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmare ștergere */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirmare Ștergere
        </DialogTitle>
        <DialogContent>
          <Typography>
            Ești sigur că vrei să ștergi acest beneficiu?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>{selectedUsage?.credite_utilizate} credite</strong> vor fi returnate elevului.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Anulează</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={confirmDeleteUsage}
            startIcon={<DeleteIcon />}
          >
            Șterge și Returnează Credite
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
